import {CommonModule} from '@angular/common';
import {Component, Inject} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {OnlyUpCredentials, PaymentProvider} from '@gatewee/types/provider';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TuiAlertService, TuiDialogContext} from '@taiga-ui/core';
import {POLYMORPHEUS_CONTEXT} from '@taiga-ui/polymorpheus';
import {Observable} from 'rxjs';
import {GateweeButtonComponent} from '../../gatewee-button/gatewee-button.component';
import {GateweeIconComponent} from '../../gatewee-icon/gatewee-icon.component';

interface DialogData {
  id: number;
  submitCredentialsFn: (
    provider: PaymentProvider,
    payload: unknown,
  ) => Observable<void>;
  provider: PaymentProvider;
}

type MtlsFileControl = 'mtlsCert' | 'mtlsKey' | 'mtlsPfx';
type MtlsGroup = 'in' | 'out';
type MtlsFileNameKey = `${MtlsGroup}.${MtlsFileControl}`;

@Component({
  selector: 'app-onlyup-credentials',
  templateUrl: './onlyup-credentials.component.html',
  styleUrls: ['./onlyup-credentials.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GateweeButtonComponent,
    GateweeIconComponent,
  ],
})
export class OnlyUpCredentialsComponent {
  form: FormGroup;
  submitting = false;
  showSecret = false;
  id!: number;
  submitCredentialsFn!: (
    provider: PaymentProvider,
    payload: unknown,
  ) => Observable<void>;
  provider!: PaymentProvider;

  fileNames: Partial<Record<MtlsFileNameKey, string>> = {};

  constructor(
    private fb: FormBuilder,
    private alerts: TuiAlertService,
    private translate: TranslateService,
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<boolean, DialogData>,
  ) {
    this.id = this.context.data.id;
    this.submitCredentialsFn = this.context.data.submitCredentialsFn;
    this.provider = this.context.data.provider;

    this.form = this.fb.group({
      sandbox: [false],
      in: this.fb.group({
        pixKey: ['', [Validators.required, Validators.minLength(3)]],
        clientId: ['', [Validators.required, Validators.minLength(3)]],
        clientSecret: ['', [Validators.required, Validators.minLength(3)]],
        mtlsCert: [null, [Validators.required]],
        mtlsKey: [null, [Validators.required]],
        mtlsPfx: [null, [Validators.required]],
        mtlsPassphrase: [null, [Validators.required]],
      }),
      out: this.fb.group({
        clientId: ['', [Validators.required, Validators.minLength(3)]],
        clientSecret: ['', [Validators.required, Validators.minLength(3)]],
        mtlsCert: [null, [Validators.required]],
        mtlsKey: [null, [Validators.required]],
        mtlsPfx: [null, [Validators.required]],
        mtlsPassphrase: [null, [Validators.required]],
      }),
    });
  }

  fileNameKey(group: MtlsGroup, controlName: MtlsFileControl): MtlsFileNameKey {
    return `${group}.${controlName}`;
  }

  async onFileChange(
    event: Event,
    group: MtlsGroup,
    controlName: MtlsFileControl,
  ): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    const control = this.form.get(group)?.get(controlName);
    const fileNameKey = this.fileNameKey(group, controlName);

    if (!file) {
      control?.setValue(null);
      this.fileNames[fileNameKey] = '';
      return;
    }

    try {
      const base64 = await this.fileToBase64(file);
      const clean = base64.split(',').pop() ?? base64;
      control?.setValue(clean);
      control?.markAsDirty();
      this.fileNames[fileNameKey] = file.name;
    } catch (e) {
      console.error(
        this.translate.instant(
          'features.integrations.credentials.fileReadError',
        ),
        e,
      );
      control?.setErrors({fileRead: true});
    }
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error);
      reader.onload = () => resolve(reader.result as string);
      reader.readAsDataURL(file);
    });
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting = true;

    const raw = this.form.value as {
      sandbox?: boolean;
      in: {
        pixKey: string;
        clientId: string;
        clientSecret: string;
        mtlsCert: string;
        mtlsKey: string;
        mtlsPfx: string;
        mtlsPassphrase: string;
      };
      out: {
        clientId: string;
        clientSecret: string;
        mtlsCert: string;
        mtlsKey: string;
        mtlsPfx: string;
        mtlsPassphrase: string;
      };
    };

    const payload: OnlyUpCredentials = {
      sandbox: raw.sandbox,
      in: {
        pixKey: raw.in.pixKey.trim(),
        clientId: raw.in.clientId.trim(),
        clientSecret: raw.in.clientSecret.trim(),
        mtlsCert: raw.in.mtlsCert,
        mtlsKey: raw.in.mtlsKey,
        mtlsPfx: raw.in.mtlsPfx,
        mtlsPassphrase: raw.in.mtlsPassphrase?.trim() ?? '',
      },
      out: {
        clientId: raw.out.clientId.trim(),
        clientSecret: raw.out.clientSecret.trim(),
        mtlsCert: raw.out.mtlsCert,
        mtlsKey: raw.out.mtlsKey,
        mtlsPfx: raw.out.mtlsPfx,
        mtlsPassphrase: raw.out.mtlsPassphrase?.trim() ?? '',
      },
    };

    let handledSuccess = false;
    const handleSuccess = () => {
      if (handledSuccess) return;
      handledSuccess = true;
      this.alerts
        .open(
          this.translate.instant(
            'features.integrations.credentials.success.message',
          ),
          {
            label: this.translate.instant(
              'features.integrations.credentials.success.title',
            ),
            appearance: 'success',
          },
        )
        .subscribe();
      this.context.completeWith(true);
    };

    this.submitCredentialsFn(this.provider, payload).subscribe({
      next: () => handleSuccess(),
      complete: () => handleSuccess(),
      error: (error: Error) => {
        console.error('Error creating API key:', error);
        this.alerts
          .open(
            this.translate.instant(
              'features.integrations.credentials.error.message',
            ),
            {
              label: this.translate.instant(
                'features.integrations.credentials.error.title',
              ),
              appearance: 'error',
            },
          )
          .subscribe();
        this.submitting = false;
      },
    });
  }

  close(): void {
    this.context.completeWith(false);
  }
}
